/**
 * Strategy V1 content normalizer (Task 050).
 *
 * `strategy_versions.content` arrives from the runtime as
 * `Readonly<Record<string, unknown>>` — the DB column is permissive
 * jsonb. The Strategy tab renders a stable document layout, so we
 * translate the wire shape into `StrategyContentV1Render` once on the
 * client.
 *
 * Design rules:
 *   - Be lenient with input. Accept the canonical key, the documented
 *     aliases (`STRATEGY_CONTENT_V1_ALIASES`), and a few opinionated
 *     fallbacks for nested shapes.
 *   - Never throw. A bad value renders as "not provided" instead of
 *     blowing up the page.
 *   - Preserve any extra keys producers chose to emit so they remain
 *     visible in the "raw" debug panel even if we don't have a typed
 *     view for them.
 *
 * The contract vocabulary itself lives in `@kmi/contracts/strategy`.
 */

import {
  STRATEGY_CONTENT_V1_ALIASES,
  STRATEGY_CONTENT_V1_SECTIONS,
  type StrategyContentV1Section,
} from '@kmi/contracts';

export interface PositioningSection {
  readonly statement: string | null;
  readonly audience: string | null;
  readonly alternative: string | null;
  readonly raw?: Readonly<Record<string, unknown>> | string | null;
}

export interface ValuePropositionSection {
  readonly headline: string | null;
  readonly body: string | null;
  readonly proof: ReadonlyArray<string>;
  readonly raw?: Readonly<Record<string, unknown>> | string | null;
}

export interface BrandValueItem {
  readonly name: string;
  readonly description: string | null;
}

export interface PersonaItem {
  readonly name: string;
  readonly demographic: string | null;
  readonly psychographic: string | null;
  readonly jobs_to_be_done: ReadonlyArray<string>;
  readonly pain_points: ReadonlyArray<string>;
}

export interface CompetitorSummaryItem {
  readonly name: string;
  readonly positioning: string | null;
  readonly strength: string | null;
  readonly weakness: string | null;
}

export interface ContentPillarItem {
  readonly name: string;
  readonly description: string | null;
  readonly cadence: string | null;
}

export interface ToneGuidelinesSection {
  readonly voice: string | null;
  readonly tone: ReadonlyArray<string>;
  readonly dos: ReadonlyArray<string>;
  readonly donts: ReadonlyArray<string>;
}

export interface ChannelRecommendationItem {
  readonly channel: string;
  readonly role: string | null;
  readonly cadence: string | null;
  readonly kpis: ReadonlyArray<string>;
}

export interface ContentMixEntry {
  readonly pillar: string;
  readonly percent: number | null;
  readonly raw?: string | null;
}

export interface RoadmapItem {
  readonly phase: string;
  readonly period: string | null;
  readonly focus: string | null;
  readonly deliverables: ReadonlyArray<string>;
}

export interface KpiTargetItem {
  readonly metric: string;
  readonly target: string | null;
  readonly period: string | null;
}

export interface DosDontsSection {
  readonly dos: ReadonlyArray<string>;
  readonly donts: ReadonlyArray<string>;
}

export interface StrategyContentV1Render {
  readonly positioning: PositioningSection | null;
  readonly value_proposition: ValuePropositionSection | null;
  readonly brand_values: ReadonlyArray<BrandValueItem>;
  readonly target_personas: ReadonlyArray<PersonaItem>;
  readonly competitor_summary: ReadonlyArray<CompetitorSummaryItem>;
  readonly content_pillars: ReadonlyArray<ContentPillarItem>;
  readonly tone_guidelines: ToneGuidelinesSection | null;
  readonly channel_recommendations: ReadonlyArray<ChannelRecommendationItem>;
  readonly monthly_content_mix: ReadonlyArray<ContentMixEntry>;
  readonly roadmap: ReadonlyArray<RoadmapItem>;
  readonly kpi_targets: ReadonlyArray<KpiTargetItem>;
  readonly dos_donts: DosDontsSection | null;
  /**
   * Keys that don't map to any known V1 section. The document view
   * surfaces them as a generic fallback block so producers can iterate
   * without losing data.
   */
  readonly unknown_sections: ReadonlyArray<{ readonly key: string; readonly value: unknown }>;
}

const SECTION_KEYS: ReadonlySet<string> = new Set(STRATEGY_CONTENT_V1_SECTIONS);

/**
 * Task 088 Phase 0 — keys that are produced by the AI pipeline /
 * strategy worker for INTERNAL use (audit, observability, V2 source-of-
 * truth, derived V0 envelope) but should NEVER reach the client-facing
 * "unknown sections" debug block. These are persisted into
 * `strategy_versions.content` by the edge function (Task 087B/D) so the
 * server has the audit trail it needs, but a marketing-team user
 * looking at their strategy should see only the editorial document, not
 * the run metadata.
 *
 * Excluding these keys from `unknown_sections` is the load-bearing fix:
 * the renderer's UnknownSectionsBlock exists to surface keys producers
 * emitted that the V0 normalizer didn't recognise. It is NOT a debug
 * dumper, and was never meant to expose internal pipeline fields.
 *
 * If a future producer adds a new internal field, ADD IT TO THIS LIST
 * — do not let it leak into the user-visible "أقسام خارج عقد V1" block.
 */
const INTERNAL_RENDER_HIDDEN_KEYS: ReadonlySet<string> = new Set([
  // V2 source-of-truth + version pin (Task 087B+).
  'brand_strategy_core_v2',
  // V1 source-of-truth (still appears on legacy rows authored before
  // Task 087B; not emitted for new V2 generation, but historic rows
  // carry it).
  'brand_strategy_v1',
  // Edge-function provenance (worker_key, model, cost, tokens, run id,
  // timestamps). The renderer has dedicated surfaces for what little
  // of this the user needs (e.g. "generated by AI on date X").
  'source_metadata',
  // Soft-validator + worker-emitted observability — bounded codes, not
  // editorial copy. Operator-facing only.
  'warnings',
  'placeholders',
  // Model self-rated confidence (0..0.85). Internal only.
  'confidence_score',
  // Derived envelope fields the V2 worker writes alongside the V0
  // surface. The V0 surface (positioning / target_personas /
  // content_pillars / tone_guidelines / dos_donts) is what the
  // renderer reads; `summary` here is the V2-specific summary
  // mirrored to top level.
  'summary',
]);

export function normalizeStrategyContent(
  content: Readonly<Record<string, unknown>>,
): StrategyContentV1Render {
  const grouped = groupBySection(content);
  return {
    positioning: parsePositioning(grouped.positioning ?? null),
    value_proposition: parseValueProposition(grouped.value_proposition ?? null),
    brand_values: parseBrandValues(grouped.brand_values ?? null),
    target_personas: parsePersonas(grouped.target_personas ?? null),
    competitor_summary: parseCompetitorSummary(grouped.competitor_summary ?? null),
    content_pillars: parseContentPillars(grouped.content_pillars ?? null),
    tone_guidelines: parseToneGuidelines(grouped.tone_guidelines ?? null),
    channel_recommendations: parseChannelRecommendations(grouped.channel_recommendations ?? null),
    monthly_content_mix: parseContentMix(grouped.monthly_content_mix ?? null),
    roadmap: parseRoadmap(grouped.roadmap ?? null),
    kpi_targets: parseKpiTargets(grouped.kpi_targets ?? null),
    dos_donts: parseDosDonts(grouped.dos_donts ?? null),
    unknown_sections: grouped.__unknown,
  };
}

interface Grouped {
  readonly __unknown: ReadonlyArray<{ readonly key: string; readonly value: unknown }>;
  positioning?: unknown;
  value_proposition?: unknown;
  brand_values?: unknown;
  target_personas?: unknown;
  competitor_summary?: unknown;
  content_pillars?: unknown;
  tone_guidelines?: unknown;
  channel_recommendations?: unknown;
  monthly_content_mix?: unknown;
  roadmap?: unknown;
  kpi_targets?: unknown;
  dos_donts?: unknown;
}

function groupBySection(content: Readonly<Record<string, unknown>>): Grouped {
  const out: Record<string, unknown> = {};
  const unknown: Array<{ key: string; value: unknown }> = [];
  // Two-pass to make canonical-wins independent of source key order:
  //   pass 1 — direct canonical keys only (`positioning`, `dos_donts`, …)
  //   pass 2 — aliases, populating only slots still empty after pass 1
  // Any value rejected because its slot was already filled by an earlier
  // direct/alias hit is surfaced in `__unknown` with the original raw
  // key, so producers can still see what was dropped.
  //
  // Task 088 Phase 0: keys in `INTERNAL_RENDER_HIDDEN_KEYS` are silently
  // dropped from BOTH passes — they are pipeline-internal (V2 source-of-
  // truth, source_metadata, warnings, placeholders, confidence_score,
  // summary) and must NEVER reach the user-facing UnknownSectionsBlock.
  for (const [rawKey, value] of Object.entries(content)) {
    const normalized = rawKey.toLowerCase().replace(/-/g, '_');
    if (INTERNAL_RENDER_HIDDEN_KEYS.has(normalized)) continue;
    if (SECTION_KEYS.has(normalized)) {
      const canonical = normalized as StrategyContentV1Section;
      if (canonical in out) {
        // Two direct entries pointing at the same canonical key — keep
        // the first; surface the rest so they're not silently lost.
        unknown.push({ key: rawKey, value });
      } else {
        out[canonical] = value;
      }
    }
  }
  for (const [rawKey, value] of Object.entries(content)) {
    const normalized = rawKey.toLowerCase().replace(/-/g, '_');
    if (INTERNAL_RENDER_HIDDEN_KEYS.has(normalized)) continue;
    if (SECTION_KEYS.has(normalized)) continue; // already handled in pass 1
    const aliased = STRATEGY_CONTENT_V1_ALIASES[normalized];
    if (aliased) {
      if (aliased in out) {
        // Canonical (or earlier alias) already filled the slot. Keep
        // the canonical value, but surface this dropped alias for
        // visibility.
        unknown.push({ key: rawKey, value });
      } else {
        out[aliased] = value;
      }
      continue;
    }
    unknown.push({ key: rawKey, value });
  }
  return { ...out, __unknown: unknown } as Grouped;
}

function parsePositioning(value: unknown): PositioningSection | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const v = value.trim();
    if (v.length === 0) return null;
    return { statement: v, audience: null, alternative: null, raw: v };
  }
  if (isPlainObject(value)) {
    return {
      statement: pickString(value, ['statement', 'positioning', 'summary']) ?? null,
      audience: pickString(value, ['audience', 'for', 'segment']) ?? null,
      alternative: pickString(value, ['alternative', 'unlike', 'vs']) ?? null,
      raw: value,
    };
  }
  return null;
}

function parseValueProposition(value: unknown): ValuePropositionSection | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const v = value.trim();
    if (v.length === 0) return null;
    return { headline: v, body: null, proof: [], raw: v };
  }
  if (isPlainObject(value)) {
    return {
      headline: pickString(value, ['headline', 'promise', 'title']) ?? null,
      body: pickString(value, ['body', 'description', 'detail']) ?? null,
      proof: pickStringArray(value, ['proof', 'evidence', 'reasons']),
      raw: value,
    };
  }
  return null;
}

function parseBrandValues(value: unknown): ReadonlyArray<BrandValueItem> {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    return splitListString(value).map((s) => ({ name: s, description: null }));
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          const v = item.trim();
          return v.length ? { name: v, description: null } : null;
        }
        if (isPlainObject(item)) {
          const name = pickString(item, ['name', 'value', 'label']);
          if (!name) return null;
          return { name, description: pickString(item, ['description', 'detail', 'note']) ?? null };
        }
        return null;
      })
      .filter((x): x is BrandValueItem => x !== null);
  }
  return [];
}

function parsePersonas(value: unknown): ReadonlyArray<PersonaItem> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!isPlainObject(item)) return null;
      const name = pickString(item, ['name', 'persona', 'label', 'title']);
      if (!name) return null;
      return {
        name,
        demographic: pickString(item, ['demographic', 'demographics']) ?? null,
        psychographic: pickString(item, ['psychographic', 'psychographics']) ?? null,
        jobs_to_be_done: pickStringArray(item, ['jobs_to_be_done', 'jtbd', 'goals']),
        pain_points: pickStringArray(item, ['pain_points', 'pains', 'frustrations']),
      };
    })
    .filter((x): x is PersonaItem => x !== null);
}

function parseCompetitorSummary(value: unknown): ReadonlyArray<CompetitorSummaryItem> {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    const v = value.trim();
    return v.length ? [{ name: v, positioning: null, strength: null, weakness: null }] : [];
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          const v = item.trim();
          return v.length ? { name: v, positioning: null, strength: null, weakness: null } : null;
        }
        if (isPlainObject(item)) {
          const name = pickString(item, ['name', 'competitor', 'label']);
          if (!name) return null;
          return {
            name,
            positioning: pickString(item, ['positioning', 'angle']) ?? null,
            strength: pickString(item, ['strength']) ?? null,
            weakness: pickString(item, ['weakness']) ?? null,
          };
        }
        return null;
      })
      .filter((x): x is CompetitorSummaryItem => x !== null);
  }
  return [];
}

function parseContentPillars(value: unknown): ReadonlyArray<ContentPillarItem> {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    return splitListString(value).map((s) => ({ name: s, description: null, cadence: null }));
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          const v = item.trim();
          return v.length ? { name: v, description: null, cadence: null } : null;
        }
        if (isPlainObject(item)) {
          const name = pickString(item, ['name', 'pillar', 'label', 'title']);
          if (!name) return null;
          return {
            name,
            description: pickString(item, ['description', 'detail', 'angle']) ?? null,
            cadence: pickString(item, ['cadence', 'frequency']) ?? null,
          };
        }
        return null;
      })
      .filter((x): x is ContentPillarItem => x !== null);
  }
  return [];
}

function parseToneGuidelines(value: unknown): ToneGuidelinesSection | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const v = value.trim();
    return v.length ? { voice: v, tone: [], dos: [], donts: [] } : null;
  }
  if (isPlainObject(value)) {
    return {
      voice: pickString(value, ['voice', 'character']) ?? null,
      tone: pickStringArray(value, ['tone', 'tones', 'attributes']),
      dos: pickStringArray(value, ['dos', 'do']),
      donts: pickStringArray(value, ['donts', 'dont', 'avoid']),
    };
  }
  return null;
}

function parseChannelRecommendations(value: unknown): ReadonlyArray<ChannelRecommendationItem> {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const mapped: ReadonlyArray<ChannelRecommendationItem | null> = value.map((item) => {
      if (typeof item === 'string') {
        const v = item.trim();
        return v.length
          ? ({
              channel: v,
              role: null,
              cadence: null,
              kpis: [],
            } satisfies ChannelRecommendationItem)
          : null;
      }
      if (isPlainObject(item)) {
        const channel = pickString(item, ['channel', 'platform', 'name']);
        if (!channel) return null;
        return {
          channel,
          role: pickString(item, ['role', 'objective', 'purpose']) ?? null,
          cadence: pickString(item, ['cadence', 'frequency']) ?? null,
          kpis: pickStringArray(item, ['kpis', 'metrics']),
        } satisfies ChannelRecommendationItem;
      }
      return null;
    });
    return mapped.filter((x): x is ChannelRecommendationItem => x !== null);
  }
  if (isPlainObject(value)) {
    const mapped: ReadonlyArray<ChannelRecommendationItem | null> = Object.entries(value).map(
      ([channel, role]) => {
        if (channel.trim().length === 0) return null;
        return {
          channel,
          role: typeof role === 'string' ? role : null,
          cadence: null,
          kpis: [],
        } satisfies ChannelRecommendationItem;
      },
    );
    return mapped.filter((x): x is ChannelRecommendationItem => x !== null);
  }
  return [];
}

function parseContentMix(value: unknown): ReadonlyArray<ContentMixEntry> {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const mapped: ReadonlyArray<ContentMixEntry | null> = value.map((item) => {
      if (!isPlainObject(item)) return null;
      const pillar = pickString(item, ['pillar', 'name', 'label']);
      if (!pillar) return null;
      const percent = pickNumber(item, ['percent', 'share', 'allocation']);
      return {
        pillar,
        percent: percent ?? null,
        raw: pickString(item, ['note', 'detail']) ?? null,
      } satisfies ContentMixEntry;
    });
    return mapped.filter((x): x is ContentMixEntry => x !== null);
  }
  if (isPlainObject(value)) {
    const mapped: ReadonlyArray<ContentMixEntry | null> = Object.entries(value).map(
      ([pillar, raw]) => {
        if (pillar.trim().length === 0) return null;
        if (typeof raw === 'number') {
          return { pillar, percent: raw, raw: null } satisfies ContentMixEntry;
        }
        if (typeof raw === 'string') {
          const trimmed = raw.trim();
          const numeric = parseFloat(trimmed.replace('%', ''));
          if (Number.isFinite(numeric)) {
            return { pillar, percent: numeric, raw: trimmed } satisfies ContentMixEntry;
          }
          return { pillar, percent: null, raw: trimmed } satisfies ContentMixEntry;
        }
        return null;
      },
    );
    return mapped.filter((x): x is ContentMixEntry => x !== null);
  }
  return [];
}

function parseRoadmap(value: unknown): ReadonlyArray<RoadmapItem> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') {
        const v = item.trim();
        return v.length ? { phase: v, period: null, focus: null, deliverables: [] } : null;
      }
      if (isPlainObject(item)) {
        const phase = pickString(item, ['phase', 'name', 'milestone', 'label']);
        if (!phase) return null;
        return {
          phase,
          period: pickString(item, ['period', 'timeline', 'when', 'quarter']) ?? null,
          focus: pickString(item, ['focus', 'objective', 'theme']) ?? null,
          deliverables: pickStringArray(item, ['deliverables', 'outputs', 'tasks']),
        };
      }
      return null;
    })
    .filter((x): x is RoadmapItem => x !== null);
}

function parseKpiTargets(value: unknown): ReadonlyArray<KpiTargetItem> {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const mapped: ReadonlyArray<KpiTargetItem | null> = value.map((item) => {
      if (typeof item === 'string') {
        const v = item.trim();
        return v.length
          ? ({ metric: v, target: null, period: null } satisfies KpiTargetItem)
          : null;
      }
      if (isPlainObject(item)) {
        const metric = pickString(item, ['metric', 'kpi', 'name']);
        if (!metric) return null;
        return {
          metric,
          target: pickString(item, ['target', 'goal', 'value']) ?? null,
          period: pickString(item, ['period', 'timeline', 'cadence']) ?? null,
        } satisfies KpiTargetItem;
      }
      return null;
    });
    return mapped.filter((x): x is KpiTargetItem => x !== null);
  }
  if (isPlainObject(value)) {
    const mapped: ReadonlyArray<KpiTargetItem | null> = Object.entries(value).map(
      ([metric, raw]) => {
        if (metric.trim().length === 0) return null;
        const target = typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : null;
        return { metric, target, period: null } satisfies KpiTargetItem;
      },
    );
    return mapped.filter((x): x is KpiTargetItem => x !== null);
  }
  return [];
}

function parseDosDonts(value: unknown): DosDontsSection | null {
  if (value === null || value === undefined) return null;
  if (isPlainObject(value)) {
    return {
      dos: pickStringArray(value, ['dos', 'do']),
      donts: pickStringArray(value, ['donts', 'dont', 'avoid']),
    };
  }
  if (typeof value === 'string') {
    const v = value.trim();
    return v.length ? { dos: [v], donts: [] } : null;
  }
  return null;
}

// ── small generic helpers ────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pickString(obj: Record<string, unknown>, keys: ReadonlyArray<string>): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed.length > 0) return trimmed;
    }
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

function pickNumber(obj: Record<string, unknown>, keys: ReadonlyArray<string>): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const numeric = parseFloat(v.trim().replace('%', ''));
      if (Number.isFinite(numeric)) return numeric;
    }
  }
  return undefined;
}

function pickStringArray(
  obj: Record<string, unknown>,
  keys: ReadonlyArray<string>,
): ReadonlyArray<string> {
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v)) {
      const items = v
        .map((item) => (typeof item === 'string' ? item.trim() : null))
        .filter((s): s is string => s !== null && s.length > 0);
      if (items.length > 0) return items;
    }
    if (typeof v === 'string') {
      const split = splitListString(v);
      if (split.length > 0) return split;
    }
  }
  return [];
}

function splitListString(value: string): ReadonlyArray<string> {
  return value
    .split(/[,،\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * True if the normalized render has at least one populated section.
 * The document view falls back to the raw key/value grid when this
 * returns false so producers that ignore the V1 vocabulary still get
 * a readable display.
 */
export function hasAnyV1Section(render: StrategyContentV1Render): boolean {
  if (render.positioning) return true;
  if (render.value_proposition) return true;
  if (render.brand_values.length > 0) return true;
  if (render.target_personas.length > 0) return true;
  if (render.competitor_summary.length > 0) return true;
  if (render.content_pillars.length > 0) return true;
  if (render.tone_guidelines) return true;
  if (render.channel_recommendations.length > 0) return true;
  if (render.monthly_content_mix.length > 0) return true;
  if (render.roadmap.length > 0) return true;
  if (render.kpi_targets.length > 0) return true;
  if (render.dos_donts) return true;
  return false;
}
